import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, ConnectWabaDto } from '../services/api';
import { ArrowLeft, Phone, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

export default function ConnectWaba() {
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ConnectWabaDto>({
    apiKey: '',
    displayPhoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!accessToken) return;

    setError(null);
    setIsLoading(true);

    try {
      const response = await apiClient.connectWaba(formData, accessToken);

      if (response.success) {
        navigate('/waba');
      } else {
        setError(response.message || 'Falha ao conectar conta WABA');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao conectar conta WABA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ConnectWabaDto, value: string) => {
    // Normalize phone number to E.164 format
    if (field === 'displayPhoneNumber') {
      // Remove all non-numeric characters except +
      let normalized = value.replace(/[^\d+]/g, '');

      // If doesn't start with +, add it
      if (normalized && !normalized.startsWith('+')) {
        normalized = '+' + normalized;
      }

      setFormData((prev) => ({ ...prev, [field]: normalized }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="min-h-screen bg-bg-1">
      <Header
        title="Conectar Conta WhatsApp Business"
        subtitle="Configure sua conexão com o Dialog360"
      >
        <button
          onClick={() => navigate('/waba')}
          className="p-2 hover:bg-bg-3 rounded-md transition-colors"
          title="Voltar"
        >
          <ArrowLeft size={20} className="text-text-2" />
        </button>
      </Header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-8">
          {/* Info Box */}
          <div className="bg-accent-1/10 border border-accent-1/20 rounded-md p-4 mb-6">
            <h3 className="text-accent-1 font-medium mb-2">
              Cadastro Simplificado
            </h3>
            <ul className="text-text-2 text-sm space-y-1 list-disc list-inside">
              <li>Você precisa ter uma conta ativa no Dialog360</li>
              <li>Informe apenas a API Key e o número de telefone</li>
              <li>O sistema buscará automaticamente todas as informações necessárias</li>
              <li>Use o formato internacional para o número (ex: +5511999999999)</li>
            </ul>
          </div>

          {error && (
            <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4">
              <p className="text-danger text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* API Key */}
            <div>
              <label
                htmlFor="apiKey"
                className="flex items-center gap-2 text-sm font-medium text-text-1 mb-2"
              >
                <Key size={16} />
                API Key do Dialog360
              </label>
              <input
                id="apiKey"
                type="text"
                value={formData.apiKey}
                onChange={(e) => handleChange('apiKey', e.target.value)}
                required
                className="w-full px-4 py-2 bg-bg-1 border border-bg-3 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-all font-mono text-sm"
                placeholder="D360-API-KEY-XXXXXXXXXXXXXX"
                disabled={isLoading}
              />
              <p className="text-text-3 text-xs mt-1">
                Encontre sua API Key no painel do Dialog360 em Configurações → API
              </p>
            </div>

            {/* Display Phone Number */}
            <div>
              <label
                htmlFor="displayPhoneNumber"
                className="flex items-center gap-2 text-sm font-medium text-text-1 mb-2"
              >
                <Phone size={16} />
                Número de Telefone
              </label>
              <input
                id="displayPhoneNumber"
                type="tel"
                value={formData.displayPhoneNumber}
                onChange={(e) => handleChange('displayPhoneNumber', e.target.value)}
                required
                className="w-full px-4 py-2 bg-bg-1 border border-bg-3 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent transition-all"
                placeholder="+5511999999999"
                disabled={isLoading}
              />
              <p className="text-text-3 text-xs mt-1">
                Número no formato internacional com código do país (o sistema buscará as informações automaticamente)
              </p>
            </div>

            {/* User Info (for reference) */}
            {user && (
              <div className="bg-bg-3/50 rounded-md p-4 border border-bg-3">
                <p className="text-text-3 text-xs mb-2">Conectando para:</p>
                <div className="flex items-center gap-2">
                  <div className="text-sm">
                    <p className="text-text-1 font-medium">{user.name}</p>
                    <p className="text-text-3 text-xs">Empresa: {user.tenantId}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/waba')}
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-bg-3 hover:bg-bg-3/80 text-text-1 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-accent-1 hover:bg-accent-2 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 focus:ring-offset-bg-2"
              >
                {isLoading ? 'Conectando...' : 'Conectar Conta'}
              </button>
            </div>
          </form>
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-bg-2 border border-bg-3 rounded-lg shadow-md p-6">
          <h3 className="text-text-1 font-semibold mb-3">
            Precisa de ajuda?
          </h3>
          <div className="space-y-2 text-sm text-text-2">
            <p>
              <strong>Onde encontro minha API Key?</strong><br />
              Acesse o painel do Dialog360, vá em Configurações → API e copie sua chave.
            </p>
            <p>
              <strong>Qual número devo informar?</strong><br />
              Use o número de telefone cadastrado no Dialog360 no formato internacional (+5511999999999).
            </p>
            <p>
              <strong>O número não está conectando?</strong><br />
              Verifique se a API Key está correta e se o número está ativo no Dialog360.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
